import { useRef, useState, useCallback, useEffect } from "react";

const ugcImages = [
  {
    id: "ig-1",
    image: "https://readdy.ai/api/search-image?query=stylish%20young%20woman%20wearing%20pearl%20shimmer%20satin%20outfit%20at%20chic%20restaurant%2C%20real%20lifestyle%20fashion%20photo%2C%20glossy%20fabric%20clothing%2C%20elegant%20evening%20look%2C%20social%20media%20influencer%20aesthetic&width=400&height=400&seq=iif_ig_001&orientation=squarish",
    handle: "@lunawearssilk",
    likes: "2.4k",
  },
  {
    id: "ig-2",
    image: "https://readdy.ai/api/search-image?query=fashionable%20woman%20in%20metallic%20silver%20dress%20at%20rooftop%20party%20at%20night%2C%20city%20lights%20background%2C%20glamorous%20fashion%20lifestyle%20photography%2C%20iridescent%20outfit%2C%20social%20media%20content&width=400&height=400&seq=iif_ig_002&orientation=squarish",
    handle: "@theshimmerfeed",
    likes: "5.1k",
  },
  {
    id: "ig-3",
    image: "https://readdy.ai/api/search-image?query=elegant%20woman%20wearing%20white%20satin%20blouse%20and%20sequin%20skirt%20at%20gallery%20opening%20event%2C%20lifestyle%20fashion%20photography%2C%20lustrous%20outfit%2C%20soft%20ambient%20lighting%2C%20chic%20style&width=400&height=400&seq=iif_ig_003&orientation=squarish",
    handle: "@glowwithcara",
    likes: "3.8k",
  },
  {
    id: "ig-4",
    image: "https://readdy.ai/api/search-image?query=beautiful%20woman%20in%20iridescent%20holographic%20outfit%20dancing%20at%20evening%20party%2C%20fashion%20editorial%20lifestyle%2C%20sparkle%20and%20shimmer%2C%20celebration%20atmosphere%2C%20real%20life%20fashion&width=400&height=400&seq=iif_ig_004&orientation=squarish",
    handle: "@noiretsatine",
    likes: "7.2k",
  },
  {
    id: "ig-5",
    image: "https://readdy.ai/api/search-image?query=stylish%20woman%20in%20metallic%20strappy%20sandals%20and%20pearl%20clutch%20bag%20at%20upscale%20dinner%2C%20detail%20accessories%20fashion%20photography%2C%20luxury%20lifestyle%2C%20lustrous%20accessories&width=400&height=400&seq=iif_ig_005&orientation=squarish",
    handle: "@theglosslife",
    likes: "1.9k",
  },
  {
    id: "ig-6",
    image: "https://readdy.ai/api/search-image?query=woman%20holding%20iridescent%20chain%20bag%20at%20evening%20event%2C%20stylish%20fashion%20close%20up%2C%20holographic%20bag%20under%20night%20lights%2C%20glamorous%20accessory%20photography%2C%20influencer%20lifestyle&width=400&height=400&seq=iif_ig_006&orientation=squarish",
    handle: "@midnightluminous",
    likes: "4.6k",
  },
];

export default function InstagramSection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, scrollLeft: 0 });

  const updateScrollState = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    el.scrollLeft = 0;
    updateScrollState();
    el.addEventListener("scroll", updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState]);

  const getCardWidth = () => {
    const el = trackRef.current;
    if (!el) return 0;
    const firstCard = el.querySelector<HTMLElement>("[data-ig-card]");
    return firstCard ? firstCard.offsetWidth + 12 : 0;
  };

  const scrollByDir = (dir: "left" | "right") => {
    const el = trackRef.current;
    if (!el) return;
    const step = getCardWidth() * 3;
    el.scrollBy({ left: dir === "left" ? -step : step, behavior: "smooth" });
  };

  const onMouseDown = (e: React.MouseEvent) => {
    const el = trackRef.current;
    if (!el) return;
    setIsDragging(true);
    dragStart.current = { x: e.pageX, scrollLeft: el.scrollLeft };
    el.style.cursor = "grabbing";
  };

  const onMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const el = trackRef.current;
    if (!el) return;
    el.scrollLeft = dragStart.current.scrollLeft - (e.pageX - dragStart.current.x);
  };

  const onMouseUp = () => {
    setIsDragging(false);
    if (trackRef.current) trackRef.current.style.cursor = "grab";
  };

  return (
    <section
      className="py-20 lg:py-28 bg-moonlight-warm"
      style={{ fontFamily: "'Jost', sans-serif" }}
    >
      {/* Header */}
      <div className="text-center px-6 mb-12">
        <p
          className="text-[10px] tracking-ultra-wide uppercase text-midnight/40 mb-3"
          style={{ letterSpacing: "0.3em" }}
        >
          Community
        </p>
        <h2
          className="text-3xl md:text-4xl font-light italic text-midnight mb-3"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          #iifglow
        </h2>
        <p className="text-sm text-midnight/50 max-w-sm mx-auto">
          Tag your looks and join a community of women who illuminate every room they enter.
        </p>
        <a
          href="https://instagram.com"
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="inline-flex items-center gap-2 mt-4 text-xs tracking-widest uppercase text-midnight/60 hover:text-midnight transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-instagram-line text-sm" /> Follow @i_if_official
        </a>
      </div>

      {/* Carousel */}
      <div className="relative px-6 lg:px-16">
        {/* Left Arrow */}
        <button
          onClick={() => scrollByDir("left")}
          className={`absolute left-6 lg:left-16 top-1/2 -translate-y-1/2 -translate-x-5 z-10
            w-10 h-10 flex items-center justify-center bg-moonlight border border-pearl
            text-midnight/60 hover:text-midnight hover:border-midnight
            transition-all duration-200 cursor-pointer whitespace-nowrap
            ${canScrollLeft ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          aria-label="Scroll left"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>

        {/* Right Arrow */}
        <button
          onClick={() => scrollByDir("right")}
          className={`absolute right-6 lg:right-16 top-1/2 -translate-y-1/2 translate-x-5 z-10
            w-10 h-10 flex items-center justify-center bg-moonlight border border-pearl
            text-midnight/60 hover:text-midnight hover:border-midnight
            transition-all duration-200 cursor-pointer whitespace-nowrap
            ${canScrollRight ? "opacity-100" : "opacity-0 pointer-events-none"}`}
          aria-label="Scroll right"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>

        {/* Left fade */}
        <div
          className={`pointer-events-none absolute left-6 lg:left-16 top-0 h-full w-16 z-[5]
            bg-gradient-to-r from-moonlight-warm to-transparent transition-opacity duration-300
            ${canScrollLeft ? "opacity-100" : "opacity-0"}`}
        />
        {/* Right fade */}
        <div
          className={`pointer-events-none absolute right-6 lg:right-16 top-0 h-full w-16 z-[5]
            bg-gradient-to-l from-moonlight-warm to-transparent transition-opacity duration-300
            ${canScrollRight ? "opacity-100" : "opacity-0"}`}
        />

        {/* Track */}
        <div
          ref={trackRef}
          className="flex gap-3 overflow-x-auto scroll-smooth select-none hide-scrollbar"
          style={{ cursor: "grab" }}
          onMouseDown={onMouseDown}
          onMouseMove={onMouseMove}
          onMouseUp={onMouseUp}
          onMouseLeave={onMouseUp}
        >
          {ugcImages.map((item) => (
            <a
              key={item.id}
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              data-ig-card
              className="group relative flex-none overflow-hidden cursor-pointer"
              style={{ width: "calc(25% - 9px)", minWidth: "180px", aspectRatio: "1/1" }}
              draggable={false}
            >
              <img
                src={item.image}
                alt={`${item.handle} wearing i-if`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                draggable={false}
              />
              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-midnight/55 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center gap-2">
                <i className="ri-instagram-line text-moonlight text-2xl" />
                <span className="text-moonlight text-[10px] tracking-widest">{item.handle}</span>
                <span className="flex items-center gap-1 text-moonlight/70 text-[10px]">
                  <i className="ri-heart-line text-xs" /> {item.likes}
                </span>
              </div>
            </a>
          ))}
        </div>
      </div>

      {/* Brand Banner */}
      <div className="mt-16 mx-6 lg:mx-16 border border-pearl/40 px-8 py-10 text-center">
        <p
          className="text-2xl md:text-3xl font-light italic text-midnight mb-4"
          style={{ fontFamily: "'Cormorant Garamond', serif" }}
        >
          &ldquo;I illuminate fashion.&rdquo;
        </p>
        <p className="text-xs tracking-widest uppercase text-midnight/40">— i-if</p>
      </div>
    </section>
  );
}
